import { FormEvent, useCallback, useEffect, useState } from "react";

import { GatewayAPIError } from "../../app/api/client";
import {
  IconBroadcast,
  IconClose,
  IconSearch,
  IconSend,
  IconUserPlus,
  IconUsers,
} from "../chats/chatIcons";
import {
  DiscoveryConversationResult,
  DiscoveryResult,
  DiscoveryUserResult,
  joinDiscoveredGroup,
  searchDiscovery,
  subscribeDiscoveredChannel,
} from "./discoveryApi";

type DiscoveryConversationKind = "group" | "channel";

export interface DiscoveryPanelAPI {
  searchDiscovery: (query: string) => Promise<DiscoveryResult[]>;
  joinDiscoveredGroup: (groupID: string) => Promise<void>;
  subscribeDiscoveredChannel: (channelID: string) => Promise<void>;
}

export interface DiscoveryFeedback {
  tone: "error" | "success";
  text: string;
}

export interface DiscoveryPanelProps {
  open?: boolean;
  initialQuery?: string;
  initialResults?: DiscoveryResult[];
  initialFeedback?: DiscoveryFeedback | null;
  loadingOverride?: boolean;
  autoLoad?: boolean;
  api?: DiscoveryPanelAPI;
  onClose: () => void;
  onStartDirectChat: (userID: string) => Promise<void> | void;
  onConversationJoined?: (chatID: string, kind: DiscoveryConversationKind) => Promise<void> | void;
  onAuthRequired?: () => void;
}

export const DISCOVERY_PANEL_CLASSES = {
  root: "discovery-panel",
  list: "discovery-list",
  row: "discovery-row",
  avatar: "discovery-avatar",
  content: "discovery-content",
  actions: "discovery-actions",
} as const;

const defaultDiscoveryPanelAPI: DiscoveryPanelAPI = {
  searchDiscovery: (query) => searchDiscovery(query),
  joinDiscoveredGroup: (groupID) => joinDiscoveredGroup(groupID),
  subscribeDiscoveredChannel: (channelID) => subscribeDiscoveredChannel(channelID),
};

function isSafePublicUsernameAlias(value: string): boolean {
  if (value.includes("@") || value.startsWith("+")) {
    return false;
  }
  if (/\s|[/\\]/.test(value)) {
    return false;
  }
  if (/^\d[\d.()_-]*$/.test(value)) {
    return false;
  }
  return value !== "";
}

function getSafeDiscoveryUsername(result: DiscoveryUserResult): string {
  const username = result.username?.trim() ?? "";
  return isSafePublicUsernameAlias(username) ? username : "";
}

export function getDiscoveryPrimaryLabel(result: DiscoveryResult): string {
  if (result.kind === "user") {
    const displayName = result.display_name.trim();
    if (displayName !== "") {
      return displayName;
    }
    const nickname = result.nickname?.trim() ?? "";
    if (nickname !== "") {
      return nickname;
    }
    return getSafeDiscoveryUsername(result) || result.user_id;
  }
  return result.name.trim() || result.chat_id;
}

export function getDiscoveryAccessibleLabel(result: DiscoveryResult): string {
  if (result.kind === "user") {
    return result.display_name.trim() || result.nickname?.trim() || result.user_id;
  }
  return result.name.trim() || result.chat_id;
}

export function deriveDiscoveryInitials(result: DiscoveryResult): string {
  const label = getDiscoveryPrimaryLabel(result);
  const parts = label.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "#";
  const second = parts.length > 1 ? parts[1]?.[0] : undefined;
  return `${first}${second ?? ""}`.toUpperCase();
}

export function mapDiscoveryActionErrorMessage(error: unknown, kind: DiscoveryConversationKind): string {
  const target = kind === "group" ? "group" : "channel";
  const action = kind === "group" ? "join this group" : "subscribe to this channel";

  if (!(error instanceof GatewayAPIError)) {
    return `Unable to ${action}.`;
  }

  if (error.code === "already_member") {
    return kind === "group" ? "You are already in this group." : "You are already subscribed to this channel.";
  }
  if (error.code === "conflict") {
    return kind === "group"
      ? "This group is closed and no longer available."
      : "This channel is closed and no longer available.";
  }
  if (error.code === "forbidden") {
    return `You do not have permission to ${action}.`;
  }
  if (error.code === "not_found") {
    return `This ${target} is no longer available.`;
  }
  if (error.code === "unauthorized") {
    return "Session expired. Sign in again.";
  }
  if (error.code === "invalid_request") {
    return "Check the discovery result and try again.";
  }
  return `Unable to ${action}.`;
}

export function mapDiscoverySearchErrorMessage(error: unknown): string {
  if (!(error instanceof GatewayAPIError)) {
    return "Unable to search discovery.";
  }
  if (error.code === "unauthorized") {
    return "Session expired. Sign in again.";
  }
  if (error.code === "invalid_request") {
    return "Enter a valid discovery search.";
  }
  return "Unable to search discovery.";
}

export function shouldRefreshDiscoveryAfterActionError(error: unknown): boolean {
  return error instanceof GatewayAPIError && (error.code === "not_found" || error.code === "conflict");
}

export async function loadDiscoveryResultsForPanel(
  query: string,
  api: Pick<DiscoveryPanelAPI, "searchDiscovery">,
): Promise<DiscoveryResult[]> {
  const normalizedQuery = query.trim();
  if (normalizedQuery === "") {
    throw new Error("query must not be empty");
  }
  return api.searchDiscovery(normalizedQuery);
}

export async function startDirectChatFromDiscovery(
  result: DiscoveryUserResult,
  onStartDirectChat: DiscoveryPanelProps["onStartDirectChat"],
): Promise<string> {
  const userID = result.user_id.trim();
  if (userID === "") {
    throw new Error("user_id must not be empty");
  }
  await onStartDirectChat(userID);
  return userID;
}

export async function joinGroupFromDiscovery(
  result: DiscoveryConversationResult,
  api: Pick<DiscoveryPanelAPI, "joinDiscoveredGroup">,
  onConversationJoined?: DiscoveryPanelProps["onConversationJoined"],
): Promise<DiscoveryFeedback> {
  if (result.kind !== "group") {
    throw new Error("discovery result must be a group");
  }
  await api.joinDiscoveredGroup(result.chat_id);
  await onConversationJoined?.(result.chat_id, "group");
  return {
    tone: "success",
    text: "Joined group.",
  };
}

export async function subscribeChannelFromDiscovery(
  result: DiscoveryConversationResult,
  api: Pick<DiscoveryPanelAPI, "subscribeDiscoveredChannel">,
  onConversationJoined?: DiscoveryPanelProps["onConversationJoined"],
): Promise<DiscoveryFeedback> {
  if (result.kind !== "channel") {
    throw new Error("discovery result must be a channel");
  }
  await api.subscribeDiscoveredChannel(result.chat_id);
  await onConversationJoined?.(result.chat_id, "channel");
  return {
    tone: "success",
    text: "Subscribed to channel.",
  };
}

function getDiscoveryTypeLabel(result: DiscoveryResult): string {
  if (result.kind === "user") {
    return "User";
  }
  if (result.kind === "group") {
    return "Group";
  }
  return "Channel";
}

function formatMemberCount(result: DiscoveryConversationResult): string {
  const label = result.kind === "channel" ? "subscriber" : "member";
  const suffix = result.member_count === 1 ? label : `${label}s`;
  return `${result.member_count} ${suffix}`;
}

function getDiscoveryDescription(result: DiscoveryResult): string {
  if (result.kind === "user") {
    return result.status_text?.trim() ?? "";
  }
  return result.description?.trim() ?? "";
}

function getDiscoverySecondaryLabel(result: DiscoveryResult): string {
  if (result.kind === "user") {
    const primary = getDiscoveryPrimaryLabel(result);
    const displayName = result.display_name.trim();
    if (displayName !== "" && displayName !== primary) {
      return displayName;
    }
    const nickname = result.nickname?.trim() ?? "";
    if (nickname !== "" && nickname !== primary) {
      return nickname;
    }
    const username = getSafeDiscoveryUsername(result);
    if (username !== "" && username !== primary) {
      return username;
    }
    return result.user_id;
  }
  return formatMemberCount(result);
}

function getJoinedStateLabel(result: DiscoveryConversationResult): string {
  return result.kind === "group" ? "Joined" : "Subscribed";
}

function isEligibleForCollaborativeAction(result: DiscoveryConversationResult): boolean {
  return result.participation === "none";
}

function markConversationJoined(
  results: DiscoveryResult[],
  chatID: string,
  kind: DiscoveryConversationKind,
): DiscoveryResult[] {
  return results.map((result) => {
    if (result.kind !== kind || result.chat_id !== chatID) {
      return result;
    }
    return {
      ...result,
      participation: kind === "group" ? "member" : "subscriber",
    };
  });
}

export function removeStaleDiscoveryConversationResult(
  results: DiscoveryResult[],
  chatID: string,
  kind: DiscoveryConversationKind,
): DiscoveryResult[] {
  const normalizedChatID = chatID.trim();
  if (normalizedChatID === "") {
    return results;
  }
  const nextResults = results.filter((result) => {
    if (result.kind !== kind) {
      return true;
    }
    return result.chat_id !== normalizedChatID;
  });
  return nextResults.length === results.length ? results : nextResults;
}

function isDirectRenderableAvatarURL(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:") ||
    normalized.startsWith("data:")
  );
}

export function DiscoveryPanel({
  open = true,
  initialQuery = "",
  initialResults = [],
  initialFeedback = null,
  loadingOverride = false,
  autoLoad = false,
  api = defaultDiscoveryPanelAPI,
  onClose,
  onStartDirectChat,
  onConversationJoined,
  onAuthRequired,
}: DiscoveryPanelProps) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<DiscoveryResult[]>(initialResults);
  const [loading, setLoading] = useState(loadingOverride);
  const [feedback, setFeedback] = useState<DiscoveryFeedback | null>(initialFeedback);
  const [actingKey, setActingKey] = useState<string | null>(null);

  const handleAuthError = useCallback(
    (error: unknown) => {
      if (error instanceof GatewayAPIError && error.code === "unauthorized") {
        onAuthRequired?.();
      }
    },
    [onAuthRequired],
  );

  const runSearch = useCallback(
    async (nextQuery: string) => {
      setLoading(true);
      setFeedback(null);
      try {
        const nextResults = await loadDiscoveryResultsForPanel(nextQuery, api);
        setResults(nextResults);
      } catch (error) {
        handleAuthError(error);
        setFeedback({
          tone: "error",
          text: mapDiscoverySearchErrorMessage(error),
        });
      } finally {
        setLoading(false);
      }
    },
    [api, handleAuthError],
  );

  const refreshAfterStaleActionError = useCallback(
    (error: unknown, result: DiscoveryConversationResult, kind: DiscoveryConversationKind) => {
      if (!shouldRefreshDiscoveryAfterActionError(error)) {
        return;
      }
      setResults((current) => removeStaleDiscoveryConversationResult(current, result.chat_id, kind));
      const normalizedQuery = query.trim();
      if (normalizedQuery === "") {
        return;
      }
      void loadDiscoveryResultsForPanel(normalizedQuery, api)
        .then((nextResults) => {
          setResults(nextResults);
        })
        .catch((refreshError) => {
          handleAuthError(refreshError);
        });
    },
    [api, handleAuthError, query],
  );

  useEffect(() => {
    if (!autoLoad || initialQuery.trim() === "") {
      return;
    }
    void runSearch(initialQuery);
  }, [autoLoad, initialQuery, runSearch]);

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void runSearch(query);
  };

  const onStartUser = async (result: DiscoveryUserResult) => {
    const actionKey = `user:${result.user_id}`;
    setActingKey(actionKey);
    setFeedback(null);
    try {
      await startDirectChatFromDiscovery(result, onStartDirectChat);
      setFeedback({
        tone: "success",
        text: "Opening direct chat.",
      });
    } catch (error) {
      handleAuthError(error);
      setFeedback({
        tone: "error",
        text: error instanceof GatewayAPIError && error.code === "unauthorized" ? "Session expired. Sign in again." : "Unable to start chat.",
      });
    } finally {
      setActingKey(null);
    }
  };

  const onJoinGroup = async (result: DiscoveryConversationResult) => {
    const actionKey = `group:${result.chat_id}`;
    setActingKey(actionKey);
    setFeedback(null);
    try {
      const nextFeedback = await joinGroupFromDiscovery(result, api, onConversationJoined);
      setResults((current) => markConversationJoined(current, result.chat_id, "group"));
      setFeedback(nextFeedback);
    } catch (error) {
      handleAuthError(error);
      refreshAfterStaleActionError(error, result, "group");
      setFeedback({
        tone: "error",
        text: mapDiscoveryActionErrorMessage(error, "group"),
      });
    } finally {
      setActingKey(null);
    }
  };

  const onSubscribeChannel = async (result: DiscoveryConversationResult) => {
    const actionKey = `channel:${result.chat_id}`;
    setActingKey(actionKey);
    setFeedback(null);
    try {
      const nextFeedback = await subscribeChannelFromDiscovery(result, api, onConversationJoined);
      setResults((current) => markConversationJoined(current, result.chat_id, "channel"));
      setFeedback(nextFeedback);
    } catch (error) {
      handleAuthError(error);
      refreshAfterStaleActionError(error, result, "channel");
      setFeedback({
        tone: "error",
        text: mapDiscoveryActionErrorMessage(error, "channel"),
      });
    } finally {
      setActingKey(null);
    }
  };

  return (
    <section
      className={`${DISCOVERY_PANEL_CLASSES.root}${open ? " discovery-panel-open" : ""}`}
      data-testid="discovery-panel"
      aria-hidden={!open}
    >
      <header className="discovery-panel-head">
        <div className="chat-shell-heading">
          <p className="chat-shell-eyebrow">Users, groups, and channels</p>
          <h2 className="chat-section-title">Discover</h2>
        </div>
        <button
          className="icon-button icon-button-ghost"
          type="button"
          onClick={onClose}
          aria-label="Close discovery"
          title="Close discovery"
        >
          <IconClose />
        </button>
      </header>

      <form className="discovery-search-form" onSubmit={onSearch}>
        <label className="chat-field discovery-search-field">
          <span>Search discovery</span>
          <input
            data-testid="discovery-search-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by nickname, name, or id"
            autoComplete="off"
            disabled={loading}
          />
        </label>
        <button
          className="icon-button icon-button-primary"
          type="submit"
          data-testid="discovery-search-submit"
          aria-label="Search discovery"
          title="Search discovery"
          disabled={loading || query.trim() === ""}
        >
          <IconSearch />
        </button>
      </form>

      {feedback !== null ? (
        <p
          className={`status-pill discovery-feedback discovery-feedback-${feedback.tone}`}
          data-testid="discovery-feedback"
          aria-live="polite"
        >
          {feedback.text}
        </p>
      ) : null}

      {loading ? (
        <p className="muted-line" data-testid="discovery-loading">
          Searching discovery...
        </p>
      ) : null}

      {!loading && results.length === 0 ? (
        <p className="muted-line" data-testid="discovery-empty">
          No discovery results yet.
        </p>
      ) : null}

      <ul className={DISCOVERY_PANEL_CLASSES.list}>
        {results.map((result) => {
          const title = getDiscoveryPrimaryLabel(result);
          const accessibleName = getDiscoveryAccessibleLabel(result);
          const secondary = getDiscoverySecondaryLabel(result);
          const description = getDiscoveryDescription(result);
          const avatarSource =
            result.kind === "user" && result.avatar_url !== undefined && isDirectRenderableAvatarURL(result.avatar_url)
              ? result.avatar_url
              : "";
          const resultKey = result.kind === "user" ? result.user_id : result.chat_id;
          const actionKey = `${result.kind}:${resultKey}`;
          const acting = actingKey === actionKey;
          return (
            <li key={`${result.kind}:${resultKey}`}>
              <article
                className={`${DISCOVERY_PANEL_CLASSES.row} discovery-row-${result.kind}`}
                data-testid={`discovery-row-${result.kind}-${resultKey}`}
              >
                <span
                  className={DISCOVERY_PANEL_CLASSES.avatar}
                  role="img"
                  aria-label={`Avatar for ${accessibleName}`}
                >
                  {avatarSource !== "" ? <img className="discovery-avatar-image" src={avatarSource} alt="" /> : deriveDiscoveryInitials(result)}
                </span>

                <span className={DISCOVERY_PANEL_CLASSES.content}>
                  <span className="discovery-title-line">
                    <span className="discovery-title">{title}</span>
                    <span className={`discovery-kind-badge discovery-kind-badge-${result.kind}`}>
                      {getDiscoveryTypeLabel(result)}
                    </span>
                  </span>
                  <span className="discovery-meta">{secondary}</span>
                  {description !== "" ? <span className="discovery-description">{description}</span> : null}
                </span>

                <span className={DISCOVERY_PANEL_CLASSES.actions}>
                  {result.kind === "user" ? (
                    <button
                      className="discovery-action-button"
                      type="button"
                      data-testid={`discovery-start-user-${result.user_id}`}
                      aria-label={acting ? `Opening chat with ${accessibleName}` : `Message ${accessibleName}`}
                      title={acting ? `Opening chat with ${accessibleName}` : `Message ${accessibleName}`}
                      onClick={() => void onStartUser(result)}
                      disabled={acting || loading}
                    >
                      <IconSend />
                      <span>Message</span>
                    </button>
                  ) : isEligibleForCollaborativeAction(result) ? (
                    <button
                      className="discovery-action-button"
                      type="button"
                      data-testid={`discovery-action-${result.chat_id}`}
                      aria-label={
                        result.kind === "group"
                          ? acting
                            ? `Joining ${accessibleName}`
                            : `Join ${accessibleName}`
                          : acting
                            ? `Subscribing to ${accessibleName}`
                            : `Subscribe to ${accessibleName}`
                      }
                      title={result.kind === "group" ? `Join ${accessibleName}` : `Subscribe to ${accessibleName}`}
                      onClick={() => void (result.kind === "group" ? onJoinGroup(result) : onSubscribeChannel(result))}
                      disabled={acting || loading}
                    >
                      {result.kind === "group" ? <IconUsers /> : <IconBroadcast />}
                      <span>{result.kind === "group" ? "Join" : "Subscribe"}</span>
                    </button>
                  ) : (
                    <span className="discovery-state-badge" data-testid={`discovery-state-${result.chat_id}`}>
                      <IconUserPlus />
                      <span>{getJoinedStateLabel(result)}</span>
                    </span>
                  )}
                </span>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
